import { useState, useMemo, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { EMAIL_TRIGGERS, EMAIL_VARIABLES } from '../data/emailTemplates';
import { renderEmail, sampleEmailContext } from '../utils/renderEmail';

const TRIGGER_BY_ID = Object.fromEntries(EMAIL_TRIGGERS.map(t => [t.id, t]));
const GROUPS = ['Booking', 'Payments', 'Agreement', 'Reminders', 'Damages'];

export default function EmailTemplatesPage() {
  const { emailTemplates, setEmailTemplates, companySettings, showToast, canEdit } = useApp();
  const editable = canEdit ? canEdit('emailtemplates') : true;

  const [selectedId, setSelectedId] = useState(emailTemplates[0]?.id || null);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody,    setDraftBody]    = useState('');
  const [showPreview,  setShowPreview]  = useState(false);
  const [dirty, setDirty] = useState(false);

  const bodyRef = useRef(null);

  const selected = useMemo(() => emailTemplates.find(t => t.id === selectedId), [emailTemplates, selectedId]);
  const trigger  = selected ? TRIGGER_BY_ID[selected.trigger] : null;

  // Sync editor with selected template
  useMemo(() => {
    if (selected) {
      setDraftSubject(selected.subject || '');
      setDraftBody(selected.body || '');
      setDirty(false);
    }
  }, [selectedId]); // eslint-disable-line

  function save() {
    setEmailTemplates(prev => prev.map(t =>
      t.id === selectedId
        ? { ...t, subject: draftSubject, body: draftBody, updatedAt: new Date().toISOString() }
        : t
    ));
    setDirty(false);
    showToast('Template saved');
  }

  function discardChanges() {
    if (selected) {
      setDraftSubject(selected.subject || '');
      setDraftBody(selected.body || '');
      setDirty(false);
    }
  }

  function toggleEnabled() {
    setEmailTemplates(prev => prev.map(t =>
      t.id === selectedId ? { ...t, enabled: !t.enabled } : t
    ));
  }

  function insertVariable(token) {
    const textarea = bodyRef.current;
    if (!textarea) {
      setDraftBody(b => b + token);
      setDirty(true);
      return;
    }
    const start = textarea.selectionStart;
    const end   = textarea.selectionEnd;
    const before = draftBody.slice(0, start);
    const after  = draftBody.slice(end);
    const next = before + token + after;
    setDraftBody(next);
    setDirty(true);
    // Restore caret after insertion
    setTimeout(() => {
      textarea.focus();
      const pos = start + token.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  }

  function insertVariableInSubject(token) {
    setDraftSubject(s => s + token);
    setDirty(true);
  }

  // Group templates by category for the sidebar
  const grouped = useMemo(() => {
    const out = {};
    emailTemplates.forEach(t => {
      const trig = TRIGGER_BY_ID[t.trigger];
      const g = trig?.group || 'Other';
      if (!out[g]) out[g] = [];
      out[g].push(t);
    });
    return out;
  }, [emailTemplates]);

  // Preview rendering
  const previewCtx = useMemo(() => sampleEmailContext(companySettings), [companySettings]);
  const preview = useMemo(() => renderEmail({ subject: draftSubject, body: draftBody }, previewCtx), [draftSubject, draftBody, previewCtx]);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>

      {/* ── Sidebar: Template list ──────────────────────────────────────── */}
      <div style={{
        width: 280, flexShrink: 0, borderRight: '1px solid var(--bd)',
        background: 'var(--sur)', overflowY: 'auto', padding: '16px 0',
      }}>
        <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--bd)', marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.3px' }}>📧 Email Templates</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4, lineHeight: 1.5 }}>
            Customize the emails sent to customers for each booking event. Use <code style={{ background: 'var(--sur2)', padding: '1px 4px', borderRadius: 3 }}>{'{{variable}}'}</code> placeholders that auto-fill at send time.
          </div>
          {/* Logo status */}
          <div style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 6,
            background: companySettings?.logoDataUrl ? 'var(--gr-l)' : 'var(--am-l)',
            border: `1px solid ${companySettings?.logoDataUrl ? 'var(--gr-b)' : 'var(--am-b)'}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {companySettings?.logoDataUrl ? (
              <>
                <img src={companySettings.logoDataUrl} alt="logo" style={{ maxHeight: 24, maxWidth: 50, objectFit: 'contain' }} />
                <div style={{ fontSize: 10, color: 'var(--gr)', lineHeight: 1.4 }}>
                  Your logo appears at the top of every email automatically.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 10, color: 'var(--am)', lineHeight: 1.4 }}>
                💡 No logo set yet. Upload one in <strong>Settings → Pro Forma → Company Identity</strong> (same logo used on pro formas). It'll then appear at the top of every email.
              </div>
            )}
          </div>
        </div>
        {GROUPS.map(group => {
          const items = grouped[group] || [];
          if (items.length === 0) return null;
          return (
            <div key={group} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 16px' }}>
                {group}
              </div>
              {items.map(t => {
                const trig = TRIGGER_BY_ID[t.trigger];
                const active = selectedId === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => {
                      if (dirty && !window.confirm('Discard unsaved changes?')) return;
                      setSelectedId(t.id);
                    }}
                    style={{
                      padding: '8px 16px', cursor: 'pointer',
                      background: active ? 'var(--ac-l)' : 'transparent',
                      borderLeft: `3px solid ${active ? 'var(--ac)' : 'transparent'}`,
                      display: 'flex', alignItems: 'center', gap: 8,
                      opacity: t.enabled ? 1 : 0.5,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{trig?.icon || '📧'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: active ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {trig?.name || t.name}
                      </div>
                      {!t.enabled && (
                        <div style={{ fontSize: 9, color: 'var(--tx3)' }}>Disabled</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── Editor ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ padding: 40, color: 'var(--tx3)', textAlign: 'center' }}>
            Pick a template on the left to edit
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--bd)', background: 'var(--sur)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22 }}>{trigger?.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{trigger?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>{trigger?.desc}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: editable ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={!!selected.enabled} onChange={editable ? toggleEnabled : undefined} disabled={!editable} style={{ accentColor: 'var(--ac)' }} />
                    {selected.enabled ? 'Enabled' : 'Disabled'}
                  </label>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowPreview(true)}>👁 Preview</button>
                  {editable && dirty && (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={discardChanges}>Discard</button>
                      <button className="btn btn-primary btn-sm" onClick={save}>💾 Save</button>
                    </>
                  )}
                </div>
              </div>
              {trigger?.attachments && trigger.attachments.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--tx3)' }}>
                  📎 Auto-attached: {trigger.attachments.join(', ')}
                </div>
              )}
            </div>

            {/* Body — two-column: editor + variables panel */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

              {/* Editor */}
              <div style={{ flex: 1, padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Subject line</label>
                  <input
                    type="text"
                    className="fi"
                    value={draftSubject}
                    onChange={e => { setDraftSubject(e.target.value); setDirty(true); }}
                    disabled={!editable}
                    placeholder="e.g. Your booking is confirmed · Ref {{booking_ref}}"
                  />
                </div>

                <div className="fg" style={{ margin: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <label className="fl">Body</label>
                  <textarea
                    ref={bodyRef}
                    className="fi"
                    value={draftBody}
                    onChange={e => { setDraftBody(e.target.value); setDirty(true); }}
                    disabled={!editable}
                    placeholder="Hi {{customer_first}},&#10;&#10;Your booking…"
                    style={{
                      flex: 1, minHeight: 320, fontFamily: "'DM Mono', monospace",
                      fontSize: 12, lineHeight: 1.6, resize: 'vertical', padding: 12,
                    }}
                  />
                </div>

                {selected.updatedAt && (
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                    Last edited: {new Date(selected.updatedAt).toLocaleString('en-GB')}
                  </div>
                )}
              </div>

              {/* Variables sidebar */}
              <div style={{
                width: 280, flexShrink: 0, borderLeft: '1px solid var(--bd)',
                background: 'var(--sur2)', overflowY: 'auto', padding: '16px 14px',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>📌 Available variables</div>
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 12, lineHeight: 1.5 }}>
                  Click to insert at cursor position in the body. Variables get replaced with real values when the email is sent.
                </div>
                {Object.entries(EMAIL_VARIABLES).map(([group, vars]) => (
                  <div key={group} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                      {group}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {vars.map(v => (
                        <button
                          key={v.token}
                          type="button"
                          onClick={() => editable && insertVariable(v.token)}
                          disabled={!editable}
                          title={v.desc}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                            background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 5,
                            padding: '5px 8px', cursor: editable ? 'pointer' : 'default',
                            textAlign: 'left', fontSize: 11, color: 'var(--tx)',
                          }}
                          onMouseEnter={e => editable && (e.currentTarget.style.background = 'var(--ac-l)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'var(--sur)')}
                        >
                          <code style={{ fontSize: 10, fontWeight: 600, color: 'var(--ac)' }}>{v.token}</code>
                          <span style={{ fontSize: 9, color: 'var(--tx3)' }}>{v.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Preview modal ──────────────────────────────────────────────── */}
      {showPreview && (
        <div
          onClick={() => setShowPreview(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 0, maxWidth: 680, width: '100%',
              maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>📧 Preview · Sample data</div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>How this email will look when sent</div>
              </div>
              <button onClick={() => setShowPreview(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', background: '#f5f5f5' }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Subject</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #eee' }}>
                  {preview.subject || <em style={{ color: '#ccc' }}>(empty subject)</em>}
                </div>
                <div dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary btn-sm" onClick={() => setShowPreview(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
